/**
 * RoomList — Vertical list of RoomCell.
 * Diffs by roomId so adds/removes/updates patch the list rather than re-rendering.
 */

import { EventEmitter } from '../../../engine/core/EventEmitter';
import { RoomCell } from './RoomCell';
import type { LobbyPlayer, LobbyRoom } from '../../../services/LobbyService';

export type PlayerLookup = (id: string) => LobbyPlayer | undefined;

interface RoomListEvents {
  'join:tap': { roomId: string };
  'profile:tap': { playerId: string };
}

export class RoomList extends EventEmitter<RoomListEvents> {
  public element: HTMLElement;
  private cells: Map<string, RoomCell> = new Map();
  private getPlayer: PlayerLookup;

  constructor(getPlayer: PlayerLookup) {
    super();
    RoomList.injectStyles();
    this.getPlayer = getPlayer;

    this.element = document.createElement('div');
    this.element.className = 'sdl-rooms';
  }

  setRooms(rooms: LobbyRoom[]): void {
    const seen = new Set<string>();
    const fragment = document.createDocumentFragment();

    rooms.forEach((room) => {
      seen.add(room.id);
      const occupants = this.resolveOccupants(room);
      let cell = this.cells.get(room.id);
      if (cell) {
        cell.update(room, occupants);
      } else {
        cell = new RoomCell(room, occupants);
        cell.on('join:tap', (e) => this.emit('join:tap', e));
        cell.on('profile:tap', (e) => this.emit('profile:tap', e));
        this.cells.set(room.id, cell);
      }
      fragment.appendChild(cell.element);
    });

    for (const [id, cell] of this.cells) {
      if (!seen.has(id)) {
        cell.destroy();
        this.cells.delete(id);
      }
    }

    this.element.appendChild(fragment);
  }

  /** Patch a single room (e.g. server says player X joined). */
  updateRoom(room: LobbyRoom): void {
    const cell = this.cells.get(room.id);
    if (cell) cell.update(room, this.resolveOccupants(room));
  }

  destroy(): void {
    this.cells.forEach((c) => c.destroy());
    this.cells.clear();
    this.element.remove();
    this.removeAllListeners();
  }

  private resolveOccupants(room: LobbyRoom): LobbyPlayer[] {
    const out: LobbyPlayer[] = [];
    room.playerIds.forEach((id) => {
      const p = this.getPlayer(id);
      if (p) out.push(p);
    });
    return out;
  }

  private static injectStyles(): void {
    if (document.getElementById('sdl-rooms-styles')) return;
    const style = document.createElement('style');
    style.id = 'sdl-rooms-styles';
    style.textContent = `
      .sdl-rooms {
        padding: 0 16px 12px;
        display: flex;
        flex-direction: column;
        gap: 10px;
      }
    `;
    document.head.appendChild(style);
  }
}
